import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Sparkles, Radio, Mail } from 'lucide-react';
import { ChatMessage as ChatMessageType, ChatAction } from '../../types/chat';
import { LeadGenProgress, LEAD_TABLE_STATUS } from '../../types';
import { apiClient } from '../../lib/api';
import { useChatActions } from '../../hooks/useChatActions';
import { useToast } from '../../hooks/useToast';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { parseError } from '../../utils/errorUtils';
import { AVAILABLE_ACTIONS } from '../../constants/chat';
import {
  useChatMessages,
  useChatInput,
  useEditableCriteria,
  useIcpAutoTrigger,
  useChatCompletion,
  IcpContext,
} from '../../hooks/chat';
import { QuickAction, MenuActions } from './shared';
import { ChatPanelEmbedded } from './core/ChatPanelEmbedded';
import { ChatPanelSliding } from './core/ChatPanelSliding';
import { ChatBottomBar } from './core/ChatBottomBar';

interface AgenticChatPanelProps {
  tableId?: string;
  tableContext?: {
    name?: string;
    type?: 'companies' | 'people' | 'custom';
    columns?: { key: string; name: string; isSegment?: boolean }[];
    data?: any;
    status?: string;
    prospectsAnalyzed?: number;
    description?: string;
    userPrompt?: string;
    criteria?: string[];
    segments?: any[];
    providerMetadata?: any;
  };
  icpContext?: IcpContext | null;
  onActionTriggered?: (action: { type: string; payload?: any }) => void;
  onExportCSV?: () => void;
  onExportExcel?: () => void;
  onShare?: () => void;
  onDelete?: () => void;
  onCreateWorkflow?: () => void;
  leadCount?: number;
  defaultOpen?: boolean;
  embedded?: boolean;
  hideHeader?: boolean;
  // Monitor flow
  showMonitorSetup?: boolean;
  onMonitorSetupComplete?: (keepMessages?: boolean) => void;
  onMonitorSetupCancel?: () => void;
  // Campaign flow
  showCampaignCreation?: boolean;
  campaignContext?: {
    totalLeadCount: number;
    filteredLeadCount?: number;
    selectedLeadCount?: number;
    selectedLeadIds?: string[];
    hasActiveFilters?: boolean;
    rows?: Array<{ id: string; data: Record<string, any>; [key: string]: any }>;
  };
  onCampaignCreated?: (campaignId: string) => void;
  onCampaignCancel?: () => void;
  onCampaignEnrichData?: (missingColumns: string[]) => void;
  onEnterRowSelectionMode?: () => void;
  onExitRowSelectionMode?: () => void;
  onSelectAllRows?: () => void;
  onSelectFirstNRows?: (n: number) => void;
  // Enrichment flow
  showEnrichmentFlow?: boolean;
  enrichmentContext?: {
    rowCount: number;
    columns: string[];
    preSelectedColumns?: string[];
    triggeredFromChat?: boolean;
  };
  onEnrichmentComplete?: (keepMessages?: boolean) => void;
  onEnrichmentCancel?: () => void;
  onEnrichmentStarted?: (columnName: string) => void;
  // Find more flow
  showFindMoreFlow?: boolean;
  findMoreContext?: {
    currentCount: number;
    initialCount?: number;
    triggeredFromChat?: boolean;
  };
  onFindMoreComplete?: (keepMessages?: boolean) => void;
  onFindMoreCancel?: () => void;
  onFindMoreStarted?: (count: number) => void;
  // Progress and initial state
  progressData?: LeadGenProgress;
  initialMessages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    criteria?: string[];
  }>;
}

export const AgenticChatPanel: React.FC<AgenticChatPanelProps> = ({
  tableId,
  tableContext,
  icpContext,
  onActionTriggered,
  onExportCSV,
  onExportExcel,
  onShare,
  onDelete,
  onCreateWorkflow,
  leadCount = 0,
  defaultOpen = false,
  embedded = false,
  hideHeader = false,
  showMonitorSetup = false,
  onMonitorSetupComplete,
  onMonitorSetupCancel,
  showCampaignCreation = false,
  campaignContext,
  onCampaignCreated,
  onCampaignCancel,
  onCampaignEnrichData,
  onEnterRowSelectionMode,
  onExitRowSelectionMode,
  onSelectAllRows,
  onSelectFirstNRows,
  showEnrichmentFlow = false,
  enrichmentContext,
  onEnrichmentComplete,
  onEnrichmentCancel,
  onEnrichmentStarted,
  showFindMoreFlow = false,
  findMoreContext,
  onFindMoreComplete,
  onFindMoreCancel,
  onFindMoreStarted,
  progressData,
  initialMessages,
}) => {
  // Panel open state
  const [isPanelOpen, setIsPanelOpen] = useState(defaultOpen);

  // Chat messages hook
  const {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isLoadingHistory,
    messagesEndRef,
    addFlowMessages,
    getSessionId,
  } = useChatMessages({ tableId, initialMessages, defaultOpen });

  // Chat input hook
  const {
    inputMessage,
    setInputMessage,
    isLoading,
    setIsLoading,
    inputRef,
    panelInputRef,
    adjustTextareaHeight,
    handleKeyPress: handleInputKeyPress,
  } = useChatInput({ tableId });

  // Editable criteria hook (for no-results scenario)
  const editableCriteria = useEditableCriteria({
    tableId,
    tableContext,
    setMessages,
  });

  const { showToast } = useToast();
  const { openSettings } = useSettingsModal();

  // ICP auto-trigger hook (effect-only)
  useIcpAutoTrigger({
    icpContext,
    tableContext,
    tableId,
    sessionId,
    setMessages,
    setSessionId,
    setIsLoading,
  });

  // Completion messages hook (effect-only)
  useChatCompletion({
    tableId,
    tableContext,
    leadCount,
    setMessages,
  });

  // Chat actions hook
  const { executeAction: originalExecuteAction } = useChatActions({
    onActionTriggered,
    tableId,
    setMessages,
    getSessionId,
  });

  // Wrapper for executeAction that handles completion message action pills
  const executeAction = useCallback((action: ChatAction) => {
    if (action.type === 'find_more') {
      onActionTriggered?.({ type: 'startFindMore' });
      return;
    }
    if (action.type === 'enrich') {
      onActionTriggered?.({ type: 'startEnrichment' });
      return;
    }
    if (action.type === 'monitor') {
      onActionTriggered?.({ type: 'startMonitorSetup' });
      return;
    }
    if (action.type === 'campaign') {
      onActionTriggered?.({ type: 'startCampaignCreation' });
      return;
    }
    originalExecuteAction(action);
  }, [originalExecuteAction, onActionTriggered]);

  // Focus panel input when panel opens
  useEffect(() => {
    if (isPanelOpen && panelInputRef.current) {
      setTimeout(() => panelInputRef.current?.focus(), 100);
    }
  }, [isPanelOpen, panelInputRef]);

  // Auto-resize textarea on input change
  useEffect(() => {
    adjustTextareaHeight(isPanelOpen ? panelInputRef.current : inputRef.current);
  }, [inputMessage, isPanelOpen, adjustTextareaHeight, panelInputRef, inputRef]);

  // Send message handler
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Open panel when sending a message (non-embedded mode)
    if (!isPanelOpen && !embedded) {
      setIsPanelOpen(true);
    }

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const contextWindow = 20;
      const recentMessages = messages.slice(-contextWindow).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const enhancedContext = {
        ...tableContext,
        availableActions: tableId
          ? AVAILABLE_ACTIONS.TABLE_ACTIONS
          : AVAILABLE_ACTIONS.DATASET_ACTIONS
      };

      const response = await apiClient.sendChatMessage(userMessage.content, enhancedContext, recentMessages, sessionId);

      if (response.session_id && !sessionId) {
        setSessionId(response.session_id);
      }

      // Determine which actions to auto-trigger vs store for manual triggering
      const autoTriggerTypes = ['find_more_results', 'enrich_column', 'send_emails'];
      const actionsToStore = response.actions?.filter(
        (action: ChatAction) => !autoTriggerTypes.includes(action.type?.toLowerCase() || '')
      ) || [];

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        ...(actionsToStore.length > 0 && { actions: actionsToStore }),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Auto-trigger workflow if backend suggests specific actions
      if (response.actions && response.actions.length > 0 && onActionTriggered) {
        const primaryAction = response.actions[0];
        const actionType = primaryAction.type?.toLowerCase();

        if (actionType === 'find_more_results') {
          onActionTriggered({ type: 'find_more_results', payload: { actionParameters: primaryAction.parameters } });
        } else if (actionType === 'enrich_column') {
          onActionTriggered({ type: 'enrich_column', payload: { actionParameters: primaryAction.parameters } });
        } else if (actionType === 'send_emails') {
          onActionTriggered({ type: 'send_emails', payload: { actionParameters: primaryAction.parameters } });
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const err = parseError(error);

      if (err.requiresUpgrade) {
        showToast(err.message, {
          type: 'error',
          duration: 8000,
          action: { label: 'Upgrade', onClick: () => openSettings('billing') }
        });
      }

      const errorMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: err.message,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, tableContext, tableId, messages, isPanelOpen, sessionId, embedded, setMessages, setInputMessage, setIsLoading, setSessionId, onActionTriggered, showToast, openSettings]);

  // Key press handler
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    handleInputKeyPress(e, handleSendMessage);
  }, [handleInputKeyPress, handleSendMessage]);

  // Quick actions
  const quickActions: QuickAction[] = useMemo(() => [
    {
      label: 'Find more',
      icon: Search,
      action: () => onFindMoreCancel && onFindMoreComplete
        ? onActionTriggered?.({ type: 'startFindMore' })
        : setInputMessage('Find 25 more results matching the same criteria')
    },
    {
      label: 'Enrich',
      icon: Sparkles,
      action: () => onEnrichmentCancel && onEnrichmentComplete
        ? onActionTriggered?.({ type: 'startEnrichment' })
        : setInputMessage('Enrich all rows with emails and LinkedIn profiles')
    },
    {
      label: 'Monitor',
      icon: Radio,
      action: () => onMonitorSetupCancel && onMonitorSetupComplete
        ? onActionTriggered?.({ type: 'startMonitorSetup' })
        : setInputMessage('Set up monitoring for this dataset')
    },
    {
      label: 'Email',
      icon: Mail,
      action: () => onCampaignCancel && onCampaignCreated
        ? onActionTriggered?.({ type: 'startCampaignCreation' })
        : setInputMessage('Send email to leads in this dataset')
    },
  ], [onActionTriggered, onFindMoreCancel, onFindMoreComplete, onEnrichmentCancel, onEnrichmentComplete, onMonitorSetupCancel, onMonitorSetupComplete, onCampaignCancel, onCampaignCreated, setInputMessage]);

  // Menu actions
  const menuActions: MenuActions = useMemo(() => ({
    onShare,
    onCreateWorkflow,
    onExportCSV,
    onExportExcel,
    onDelete,
  }), [onShare, onCreateWorkflow, onExportCSV, onExportExcel, onDelete]);

  // Editable criteria props for embedded component
  const editableCriteriaProps = useMemo(() => ({
    criteria: editableCriteria.editableCriteria,
    editingIndex: editableCriteria.editingCriterionIndex,
    editingValue: editableCriteria.editingCriterionValue,
    hasEdited: editableCriteria.hasEditedCriteria,
    isCreating: editableCriteria.isCreatingEditedSearch,
    editInputRef: editableCriteria.editInputRef,
    onEdit: editableCriteria.handleEditCriterion,
    onSave: editableCriteria.handleSaveCriterionEdit,
    onCancel: editableCriteria.handleCancelCriterionEdit,
    onRemove: editableCriteria.handleRemoveCriterion,
    onRun: editableCriteria.handleRunEditedSearch,
    onEditingValueChange: editableCriteria.setEditingCriterionValue,
  }), [editableCriteria]);

  // Embedded mode
  if (embedded) {
    return (
      <ChatPanelEmbedded
        tableId={tableId}
        tableContext={tableContext}
        messages={messages}
        isLoading={isLoading}
        isLoadingHistory={isLoadingHistory}
        inputMessage={inputMessage}
        onInputChange={setInputMessage}
        onSend={handleSendMessage}
        onKeyPress={handleKeyPress}
        onExecuteAction={executeAction}
        panelInputRef={panelInputRef}
        messagesEndRef={messagesEndRef}
        adjustTextareaHeight={adjustTextareaHeight}
        hideHeader={hideHeader}
        menuActions={menuActions}
        quickActions={quickActions}
        progressData={progressData}
        editableCriteriaProps={
          tableContext?.status === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS && editableCriteria.editableCriteria.length > 0
            ? editableCriteriaProps
            : undefined
        }
        // Campaign flow
        showCampaignCreation={showCampaignCreation}
        campaignContext={campaignContext}
        onCampaignCreated={onCampaignCreated}
        onCampaignCancel={onCampaignCancel}
        onCampaignEnrichData={onCampaignEnrichData}
        onEnterRowSelectionMode={onEnterRowSelectionMode}
        onExitRowSelectionMode={onExitRowSelectionMode}
        onSelectAllRows={onSelectAllRows}
        onSelectFirstNRows={onSelectFirstNRows}
        // Monitor flow
        showMonitorSetup={showMonitorSetup}
        onMonitorSetupComplete={onMonitorSetupComplete}
        onMonitorSetupCancel={onMonitorSetupCancel}
        // Enrichment flow
        showEnrichmentFlow={showEnrichmentFlow}
        enrichmentContext={enrichmentContext}
        onEnrichmentStarted={onEnrichmentStarted}
        onAddFlowMessages={addFlowMessages}
        // Find more flow
        showFindMoreFlow={showFindMoreFlow}
        findMoreContext={findMoreContext}
        onFindMoreStarted={onFindMoreStarted}
      />
    );
  }

  // Non-embedded mode: sliding panel + bottom bar
  return (
    <>
      <ChatPanelSliding
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        messages={messages}
        isLoading={isLoading}
        isLoadingHistory={isLoadingHistory}
        inputMessage={inputMessage}
        onInputChange={setInputMessage}
        onSend={handleSendMessage}
        onKeyPress={handleKeyPress}
        onExecuteAction={executeAction}
        panelInputRef={panelInputRef}
        messagesEndRef={messagesEndRef}
        adjustTextareaHeight={adjustTextareaHeight}
      />
      {!isPanelOpen && (
        <ChatBottomBar
          inputMessage={inputMessage}
          onInputChange={setInputMessage}
          onSend={handleSendMessage}
          onKeyPress={handleKeyPress}
          isLoading={isLoading}
          quickActions={quickActions}
          messageCount={messages.length}
          onOpenPanel={() => setIsPanelOpen(true)}
          menuActions={menuActions}
          inputRef={inputRef}
          adjustTextareaHeight={adjustTextareaHeight}
        />
      )}
    </>
  );
};
